import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

/**
 * Shared analytical panel shell for chart, heatmap, and report blocks.
 *
 * The component owns the bordered surface, title/subtitle rhythm, optional
 * title icon, and top-right action slot so repeated analytical blocks do not
 * drift into slightly different header layouts.
 */
export default function InsightPanel({
  title,
  subtitle,
  accent,
  titleIcon,
  actions,
  children,
}: {
  title: string;
  subtitle?: string;
  accent: string;
  titleIcon?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '18px',
        border: `1px solid ${alpha(accent, 0.18)}`,
        backgroundColor: alpha(theme.palette.background.paper, 0.62),
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems="flex-start"
        gap={1.4}
        sx={{ mb: 1.8, minWidth: 0 }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          gap={0.8}
          sx={{
            minWidth: 0,
            width: { xs: '100%', md: 'auto' },
            flex: { xs: '1 1 auto', md: actions ? '0 1 440px' : '1 1 auto' },
            maxWidth: { md: actions ? 460 : 'none' },
          }}
        >
          {titleIcon ? (
            <Box
              aria-hidden="true"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: accent,
                '& svg': {
                  fontSize: 18,
                },
              }}
            >
              {titleIcon}
            </Box>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                letterSpacing: '0.12em',
                fontWeight: 700,
                lineHeight: 1.45,
                display: 'block',
              }}
            >
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
        {actions ? (
          <Stack
            direction="row"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            alignItems="center"
            gap={1}
            flexWrap="wrap"
            useFlexGap
            sx={{
              flex: { xs: '1 1 auto', md: '1 1 520px' },
              minWidth: { xs: '100%', md: 0 },
              maxWidth: { xs: '100%', md: 'calc(100% - 320px)' },
              ml: { md: 'auto' },
              pt: { md: 0.15 },
              '& .MuiChip-root': {
                maxWidth: '100%',
              },
            }}
          >
            {actions}
          </Stack>
        ) : null}
      </Stack>

      {children}
    </Box>
  );
}
